/**
 * Lazy-загружаемые страницы.
 *
 * Все 28 не-критичных страниц обёрнуты в React.lazy(). Каждая грузится только
 * когда юзер открывает соответствующий роут — это даёт code splitting по страницам,
 * initial bundle не раздувается.
 *
 * Eager-страницы (WelcomePage, FeedPage) — в самом appRouter.tsx.
 *
 * ─── Почему отдельный файл? ───
 * ESLint правило react-refresh/only-export-components требует чтобы файл
 * экспортил ЛИБО только компоненты, ЛИБО только не-компоненты. В appRouter.tsx
 * есть `export const router` (объект, не компонент) — поэтому lazy-компоненты
 * вынесены сюда для корректного HMR в dev-режиме.
 *
 * ─── Паттерн `.then(m => ({ default: m.XPage })) ───
 * Все наши страницы — named exports (`export { XPage }`), а React.lazy ждёт
 * default export. Оборачиваем через .then для совместимости.
 *
 * ─── Почему lazy на module scope ───
 * React Router v7 changelog указывает что lazy внутри компонента несовместим
 * с useTransition — может привести к двойной загрузке чанков.
 */

import { lazy } from 'react';

// ─── Auth страницы ───
export const SignInPage = lazy(() =>
  import('@/pages/sign-in').then((m) => ({ default: m.SignInPage })),
);
export const SignUpPage = lazy(() =>
  import('@/pages/sign-up').then((m) => ({ default: m.SignUpPage })),
);
export const ForgotPasswordPage = lazy(() =>
  import('@/pages/forgot-password').then((m) => ({ default: m.ForgotPasswordPage })),
);
export const ResetPasswordPage = lazy(() =>
  import('@/pages/reset-password').then((m) => ({ default: m.ResetPasswordPage })),
);

// ─── Остальные home-табы (кроме /feed — он eager) ───
export const SelectionsPage = lazy(() =>
  import('@/pages/home-selections').then((m) => ({ default: m.SelectionsPage })),
);
export const PopularPage = lazy(() =>
  import('@/pages/home-popular').then((m) => ({ default: m.PopularPage })),
);
export const HomeWishlistsPage = lazy(() =>
  import('@/pages/home-wishlists').then((m) => ({ default: m.HomeWishlistsPage })),
);

// ─── Основные страницы ───
export const WishlistPage = lazy(() =>
  import('@/pages/wishlist').then((m) => ({ default: m.WishlistPage })),
);
export const CommunityPage = lazy(() =>
  import('@/pages/community').then((m) => ({ default: m.CommunityPage })),
);
export const UserProfilePage = lazy(() =>
  import('@/pages/user-profile').then((m) => ({ default: m.UserProfilePage })),
);
export const WishlistDetailPage = lazy(() =>
  import('@/pages/wishlist-detail').then((m) => ({ default: m.WishlistDetailPage })),
);
export const WishlistItemDetailPage = lazy(() =>
  import('@/pages/wishlist-item-detail').then((m) => ({
    default: m.WishlistItemDetailPage,
  })),
);
export const NotFoundPage = lazy(() =>
  import('@/pages/not-found').then((m) => ({ default: m.NotFoundPage })),
);
export const DebugColorsPage = lazy(() =>
  import('@/pages/debug-colors').then((m) => ({ default: m.DebugColorsPage })),
);

// ─── Settings ───
export const EditProfilePage = lazy(() =>
  import('@/pages/settings-edit-profile').then((m) => ({ default: m.EditProfilePage })),
);
export const SettingsPage = lazy(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsPage })),
);
export const NotificationsPage = lazy(() =>
  import('@/pages/notifications').then((m) => ({ default: m.NotificationsPage })),
);
export const SettingsLanguagePage = lazy(() =>
  import('@/pages/settings-language').then((m) => ({ default: m.SettingsLanguagePage })),
);
export const SettingsCurrencyPage = lazy(() =>
  import('@/pages/settings-currency').then((m) => ({ default: m.SettingsCurrencyPage })),
);
export const ChangePasswordPage = lazy(() =>
  import('@/pages/settings-change-password').then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
export const AppNotificationsPage = lazy(() =>
  import('@/pages/settings-app-notifications').then((m) => ({
    default: m.AppNotificationsPage,
  })),
);
export const BlockedUsersPage = lazy(() =>
  import('@/pages/settings-blocked-users').then((m) => ({ default: m.BlockedUsersPage })),
);
export const TermsPage = lazy(() =>
  import('@/pages/settings-terms').then((m) => ({ default: m.TermsPage })),
);
export const PrivacyPolicyPage = lazy(() =>
  import('@/pages/settings-privacy-policy').then((m) => ({
    default: m.PrivacyPolicyPage,
  })),
);
export const SupportPage = lazy(() =>
  import('@/pages/settings-support').then((m) => ({ default: m.SupportPage })),
);

// ─── Формы wishlist/item ───
export const WishlistFormPage = lazy(() =>
  import('@/pages/wishlist-form').then((m) => ({ default: m.WishlistFormPage })),
);
export const WishlistItemFormPage = lazy(() =>
  import('@/pages/wishlist-item-form').then((m) => ({ default: m.WishlistItemFormPage })),
);

// ─── Friend ───
export const FriendProfilePage = lazy(() =>
  import('@/pages/friend-profile').then((m) => ({ default: m.FriendProfilePage })),
);
export const FriendWishlistsPage = lazy(() =>
  import('@/pages/friend-wishlists').then((m) => ({ default: m.FriendWishlistsPage })),
);

// ─── Items ───
export const MyItemsPage = lazy(() =>
  import('@/pages/my-items').then((m) => ({ default: m.MyItemsPage })),
);
export const UserItemsPage = lazy(() =>
  import('@/pages/user-items').then((m) => ({ default: m.UserItemsPage })),
);

// ─── Secret Santa ───
export const SecretSantaFormPage = lazy(() =>
  import('@/pages/secret-santa-form').then((m) => ({ default: m.SecretSantaFormPage })),
);
export const SecretSantaDetailPage = lazy(() =>
  import('@/pages/secret-santa-detail').then((m) => ({
    default: m.SecretSantaDetailPage,
  })),
);
export const SecretSantaListPage = lazy(() =>
  import('@/pages/secret-santa-list').then((m) => ({ default: m.SecretSantaListPage })),
);

// ─── Bookings ───
export const BookingsListPage = lazy(() =>
  import('@/pages/bookings-list').then((m) => ({ default: m.BookingsListPage })),
);

// ─── Подборки ───
export const SelectionDetailPage = lazy(() =>
  import('@/pages/selection-detail').then((m) => ({ default: m.SelectionDetailPage })),
);
export const SelectionItemDetailPage = lazy(() =>
  import('@/pages/selection-item-detail').then((m) => ({
    default: m.SelectionItemDetailPage,
  })),
);
